import { Form, FormItem, useForm } from '@magicdoor/form';
import { Tooltip, useLocalization } from '@magicdoor/ui';
import { createMemo, onMount, Show } from 'solid-js';
import IconInformation from '~/assets/images/common/info.svg?component-solid';
import { DropdownMenu } from '~/components/common/DropdownMenu';
import { LabeledTextInput } from '~/components/common/Inputs/LabeledTextInput';
import { BankAccountConnectionStatus, PayBillType } from '~/swagger/Api';
import { cn } from '~/utils/classnames';
import { paymentTypeOptions, transactionPaymentMethodsDict } from '~/utils/constant';
import type { BetterForm } from '@magicdoor/form';
import type { ComponentProps, ValidComponent } from 'solid-js';
import type { Option } from '~/components/common/DropdownMenu';
import type { PlaidConnectedStatusDto, TransactionPaymentMethod } from '~/swagger/Api';

export interface PaymentSelectionValue {
  paymentBankAccountId?: string;
  receivingBankAccountId?: string;
  paymentType: PayBillType;
  paymentMethod?: TransactionPaymentMethod;
  externalTransactionId?: string;
  memo?: string;
}

export interface PaymentSelectionOptions {
  paymentAccountOptions: Array<Option & { plaid?: PlaidConnectedStatusDto }>;
  receivingPlaidAccountOptions?: Option[];
}

export interface PaymentSelectionLabels {
  paymentAccount?: string;
  paymentType?: string;
  paymentTypeTooltip: string;
  paymentMethod?: string;
  receivingAccount?: string;
  externalTransactionId?: string;
  memo?: string;
  memoPlaceholder?: string;
  externalTransactionIdPlaceholder?: string;
}

interface PaymentSelectionGroupProps {
  value?: PaymentSelectionValue;
  onInput?: (value: PaymentSelectionValue) => void;
  options: PaymentSelectionOptions;
  labels?: PaymentSelectionLabels;
  disabled?: boolean;
  class?: string;
  form?: BetterForm.form;
  variant?: 'bordered';
}

const wrapper =
  <T extends ValidComponent>(Component: T, defaultProps: Partial<ComponentProps<T>>) =>
  (props: ComponentProps<T>) => {
    const allProps = createMemo(() => {
      return { ...props, ...defaultProps };
    });
    // @ts-expect-error allProps is valid
    return <Component {...allProps()} />;
  };

export const PaymentSelectionGroup = (props: PaymentSelectionGroupProps) => {
  const { t } = useLocalization();

  const form = createMemo(
    () =>
      props.form ??
      useForm({
        validateTriggers: ['onChange'],
      })
  );

  const mergedLabels = createMemo(() => {
    return Object.assign(
      {},
      {
        paymentAccount: t('Payment Account'),
        paymentType: t('Payment Type'),
        paymentMethod: t('Payment Method'),
        receivingAccount: t('Receiving account'),
        externalTransactionId: t('External transaction ID'),
        memo: t('Memo'),
      },
      props.labels
    );
  });

  const processedPaymentTypeOptions = createMemo(() => paymentTypeOptions(t));

  const isManualPayment = createMemo(() => form()?.formStore?.paymentType === PayBillType.Manual);

  const dropdownClass = createMemo(() => {
    if (props.variant === 'bordered') {
      return undefined;
    }
    return 'border-none bg-transparent px-0 font-medium !ring-0';
  });

  const inputClass = createMemo(() => {
    if (props.variant === 'bordered') {
      return undefined;
    }
    return '!border-none bg-white !ring-0';
  });

  const handleChange = () => {
    props.onInput?.({
      paymentBankAccountId: form()?.formStore?.paymentBankAccountId,
      receivingBankAccountId: form()?.formStore?.receivingBankAccountId,
      paymentType: form()?.formStore?.paymentType,
      paymentMethod: form()?.formStore?.paymentMethod,
      externalTransactionId: form()?.formStore?.externalTransactionId,
      memo: form()?.formStore?.memo,
    });
  };

  onMount(() => {
    if (props.value) {
      form().setFormStore(props.value);
      form().validateForm();
    }
  });

  const PaymentTypeComponent = wrapper(DropdownMenu, {
    labelJSX: (
      <Tooltip message={mergedLabels().paymentTypeTooltip}>
        <div class="flex items-center gap-1">
          <span>{mergedLabels().paymentType}</span>
          <IconInformation class="text-text-level02" />
        </div>
      </Tooltip>
    ),
  });

  return (
    <Form defaultForm={form()} class={cn('rounded-lg bg-[#FAE9FF] p-3', props.class)} onFieldChange={handleChange}>
      <div class={cn('mb-4 grid grid-cols-3 gap-3', { 'grid-cols-2': form()?.formStore?.paymentType === PayBillType.PrintCheck })}>
        <div class={cn('col-span-1 rounded-lg bg-white px-3 pt-2.5', { 'bg-white/50': props.disabled })}>
          <FormItem
            component={DropdownMenu}
            label={mergedLabels().paymentAccount}
            formFieldName="paymentBankAccountId"
            labelClass="mb-0"
            buttonClass={dropdownClass()}
            disabled={props.disabled}
            options={props.options.paymentAccountOptions}
            placeholder={t('Please select')}
            onChangeMethodName="onChange"
            rules={[
              {
                validator: (value) => {
                  return !(form().formStore.paymentType === PayBillType.Ach && !value);
                },
                message: t('Payment account is required'),
              },
              {
                validator: (value) => {
                  const curr = props.options.paymentAccountOptions.find((option) => option.value === value);
                  const paymentTypeAch = form().formStore.paymentType === PayBillType.Ach;
                  if (!paymentTypeAch) return true;
                  return !!(curr && curr.plaid?.status === BankAccountConnectionStatus.Approved);
                },
                message: t('The payment account is not connected to Plaid'),
              },
            ]}
          />
        </div>
        <div class={cn('col-span-1 rounded-lg bg-white px-3 pt-2.5', { 'bg-white/50': props.disabled })}>
          <FormItem
            component={PaymentTypeComponent}
            formFieldName="paymentType"
            labelClass="mb-0"
            buttonClass={dropdownClass()}
            placeholder={t('Please select')}
            disabled={props.disabled}
            options={processedPaymentTypeOptions()}
            onChangeMethodName="onChange"
            rules={[{ required: true, message: t('{name} is required', { name: mergedLabels().paymentType }) }]}
          />
        </div>
        <Show when={isManualPayment()}>
          <div class={cn('col-span-1 rounded-lg bg-white px-3 pt-2.5', { 'bg-white/50': props.disabled })}>
            <FormItem
              component={DropdownMenu}
              formFieldName="paymentMethod"
              label={mergedLabels().paymentMethod}
              labelClass="mb-0"
              buttonClass={dropdownClass()}
              disabled={props.disabled}
              options={transactionPaymentMethodsDict.getOptions()}
              placeholder={t('Please select')}
              onChangeMethodName="onChange"
              removeValueOnClean
              rules={[{ required: true, message: t('{name} is required', { name: mergedLabels().paymentMethod }) }]}
            />
          </div>
        </Show>
        <Show when={form()?.formStore?.paymentType === PayBillType.Ach}>
          <div class={cn('col-span-1 rounded-lg bg-white px-3 pt-2.5', { 'bg-white/50': props.disabled })}>
            <FormItem
              component={DropdownMenu}
              formFieldName="receivingBankAccountId"
              label={mergedLabels().receivingAccount}
              labelClass="mb-0"
              buttonClass={dropdownClass()}
              disabled={props.disabled}
              options={props.options.receivingPlaidAccountOptions}
              placeholder={t('Please select')}
              onChangeMethodName="onChange"
              rules={[{ required: true, message: t('{name} is required', { name: mergedLabels().receivingAccount }) }]}
              removeValueOnClean
            />
          </div>
        </Show>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <Show when={isManualPayment()}>
          <div class="col-span-1">
            <FormItem
              component={LabeledTextInput}
              formFieldName="externalTransactionId"
              label={mergedLabels().externalTransactionId}
              inputContainerClass={cn(inputClass(), { 'bg-white/50': props.disabled })}
              placeholder={mergedLabels().externalTransactionIdPlaceholder}
              disabled={props.disabled}
              maxlength={100}
              rules={[{ required: true, message: t('{name} is required', { name: mergedLabels().externalTransactionId }) }]}
              eventTriggerName="onInput"
              removeValueOnClean
            />
          </div>
        </Show>
        <div class={cn('col-span-2', { 'col-span-3': !isManualPayment() })}>
          <FormItem
            component={LabeledTextInput}
            formFieldName="memo"
            label={mergedLabels().memo}
            inputContainerClass={cn(inputClass(), { 'bg-white/50': props.disabled })}
            placeholder={mergedLabels().memoPlaceholder}
            disabled={props.disabled}
            maxlength={500}
            rules={[
              {
                length: [0, 500],
                message: t('{name} must be between {min} and {max} characters', { name: mergedLabels().memo, min: 1, max: 500 }),
              },
            ]}
          />
        </div>
      </div>
    </Form>
  );
};

export const usePaymentSelectionForms = () => {
  const formsMap = new Map<string, BetterForm.form>();
  const getForm = (entityId?: string) => {
    if (!entityId) {
      return;
    }

    const _form = formsMap.get(entityId);
    if (_form) {
      return _form;
    }
    const form = useForm({
      validateTriggers: ['onChange'],
    });
    formsMap.set(entityId, form);
    return form;
  };

  const hasPaymentCheckError = () => {
    for (const form of formsMap.values()) {
      const { validateStatus } = form.validateForm();
      if (!validateStatus) {
        return true;
      }
    }
  };

  const clearForm = (entityId?: string) => {
    if (!entityId) {
      return formsMap.clear();
    }
    formsMap.delete(entityId);
  };

  return {
    getForm,
    hasPaymentCheckError,
    clearForm,
  };
};
